import {
  LinkProps,
  ListItem,
  ListItemButtonProps,
  ListItemText,
  ListItemTextProps,
  Stack,
} from '@mui/material'
import { FC, ReactNode, useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  ArtistRemovePostCategoryInput,
  ArtistUpdatePostCategoryInput,
  PostCategoryDetail,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { Cell } from '@/core/architecture'

import { DeleteCategoryModal } from './deleteCategoryModal'
import { UpdatePostCategoryModal } from './updatePostCategoryModal'

export * from './fragments'

type CategoryBoxProps = {
  category: PostCategoryDetail
  divider?: boolean
  onUpdateCategory: (inputs: ArtistUpdatePostCategoryInput) => Promise<void>
  onDeleteCategory: (inputs: ArtistRemovePostCategoryInput) => Promise<void>
}

export const CategoryBox: Cell<CategoryBoxProps> = ({
  category,
  divider,
  onDeleteCategory,
  onUpdateCategory,
}) => {
  const [openUpdate, setOpenUpdate] = useState<boolean>(false)
  const toggleUpdateDrawer = useCallback(
    (open: boolean) => () => {
      setOpenUpdate(open)
    },
    []
  )
  const [openDelete, setOpenDelete] = useState<boolean>(false)

  const toggleDeleteDrawer = useCallback(
    (open: boolean) => () => {
      setOpenDelete(open)
    },
    []
  )

  return (
    <>
      <CategoryMenuListItem
        sx={{
          height: 'auto',
          gridGap: 0,
          py: 4,
          color: 'text.primary',
          width: '90vw',
          maxWidth: 550,
        }}
        title={category.name}
        primaryTypographyProps={{
          paddingRight: 8,
          variant: 'h5',
        }}
        subTitle={category.description}
        secondaryTypographyProps={{
          variant: 'body2',
          sx: {
            ...styleSets.lineCamp(1),
          },
        }}
        divider={divider}
        secondaryAction={
          <>
            <IconButton.Pencil
              size="small"
              onClick={toggleUpdateDrawer(true)}
            />
            <IconButton.Trash size="small" onClick={toggleDeleteDrawer(true)} />
          </>
        }
        disableRipple
        disableTouchRipple
      />
      <UpdatePostCategoryModal
        open={openUpdate}
        onClose={toggleUpdateDrawer(false)}
        onUpdateCategory={onUpdateCategory}
        category={category}
      />
      <DeleteCategoryModal
        open={openDelete}
        onClose={toggleDeleteDrawer(false)}
        onDeleteCategory={onDeleteCategory}
        id={category.id}
      />
    </>
  )
}

type CategoryMenuListItem = {
  title: ListItemTextProps['primary']
  subTitle?: ListItemTextProps['secondary']
  primaryTypographyProps?: ListItemTextProps['primaryTypographyProps']
  secondaryTypographyProps?: ListItemTextProps['secondaryTypographyProps']
  secondaryAction?: ReactNode
  component?: string | ReactNode
  target?: LinkProps['target']
} & Omit<ListItemButtonProps, 'title'>

const CategoryMenuListItem: FC<CategoryMenuListItem> = ({
  title,
  subTitle,
  sx,
  primaryTypographyProps,
  secondaryTypographyProps,
  secondaryAction,
}) => {
  return (
    <ListItem
      sx={{
        display: 'grid',
        gridTemplateColumns: '80% auto',
        gridGap: 16,
        height: { xs: 48, md: 56 },
        px: { xs: 6, md: 7 },
        py: { xs: 2, md: 4 },
        boxSizing: 'border-box',
        ...sx,
      }}
    >
      <ListItemText
        primary={title}
        primaryTypographyProps={{
          variant: 'body2',
          paddingRight: 8,
          ...primaryTypographyProps,
        }}
        secondary={subTitle}
        secondaryTypographyProps={{
          variant: 'subtitle2',
          ...secondaryTypographyProps,
        }}
      />
      {secondaryAction && (
        <Stack
          direction="row"
          spacing={3}
          justifyContent={'flex-end'}
          alignSelf={'flex-end'}
        >
          {secondaryAction}
        </Stack>
      )}
    </ListItem>
  )
}
